import {getLocalStorageItem, setLocalStorageItem} from '../localStorage';
import {once} from 'lodash';
import amplitude from 'amplitude-js';
import appboy from 'appboy-web-sdk/appboy.core.min';
import fetchUser from './fetchUser';
import * as Sentry from '@sentry/browser';

export function isOptedOut(user: User) {
    return user.isAuthenticated && !user.webAnalyticsEnabled;
}

if (!process.env.DEBUG) {
    Sentry.init({
        dsn:
            'https://eebe259ebaa846d39aaae0e3404505ab@o166208.ingest.sentry.io/1238795',
    });
}

export const initialize = once(async () => {
    const user = await fetchUser();

    const ga = (window as any).ga;

    if (Gaia.isSU || isOptedOut(user)) return;

    const amplitudeInstance = amplitude.getInstance();

    // Monkeypatch amplitude's storage layer to ensure it does not use cookies.
    // Cookies which change on every request prevent HTTP caching.
    // @ts-ignore
    amplitudeInstance.cookieStorage = {
        reset: () => {},
        options: () => ({}),
        get: (name: string) => getLocalStorageItem<any>(name, undefined),
        set: (name: string, value: any) =>
            setLocalStorageItem<any>(name, value),
        remove: (name: string) => setLocalStorageItem<any>(name, undefined),
    };

    const configOptions = {
        includeReferrer: true,
        includeUtm: true,
        includeGclid: true,
        deviceId: user.amplitudeDeviceId,
    };
    amplitudeInstance.init(Gaia.amplitudeApiKey, undefined, configOptions);

    appboy.initialize(Gaia.blazeSdkKey, {
        baseUrl: 'https://customer.iad-03.braze.com/api/v3',
        enableLogging: false,
        // Use localStorage instead of cookies.
        // Cookies which change on every request prevent HTTP caching.
        noCookies: true,
    });
    appboy.openSession();

    // This is exclusively for the legacy angular code. Do not use otherwise.
    (window as any).appboy = appboy;
    (window as any).amplitude = amplitude;

    ga('create', Gaia.googleTrackingKey, 'auto');
    ga('send', 'pageview');

    amplitude.getInstance().setUserId(user.id);
    ga('set', 'userId', user.id);
    appboy.changeUser(user.id);

    if (!process.env.DEBUG) {
        Sentry.setUser({id: user.id, email: user.email});
    }

    // Store referrer & landing page
    if (
        !user.isAuthenticated &&
        document.cookie.search('referrer') < 0 &&
        document.cookie.search('landing_page') < 0
    ) {
        // Store referrer only the first time a user hits the website + landing page
        document.cookie = 'referrer=' + document.referrer + ';path=/';
        document.cookie = 'landing_page=' + location.href + ';path=/';
        trackAnalytics('Set Landing Page', {page: location.href});
    }
});

initialize();

export default async function trackAnalytics<T extends AnalyticsEvent>(
    name: T['analyticsEventName'],
    properties?: Omit<T, 'analyticsEventName'>,
    hitCallback?: () => void
) {
    await initialize();
    const ga = (window as any).ga;

    if (Gaia.isSU || isOptedOut(await fetchUser())) return;

    // Amplitude complains about empty label property
    if (
        properties &&
        ((properties as any).label === null || (properties as any).label === '')
    ) {
        delete (properties as any)['label'];
    }

    amplitude.getInstance().logEvent(name, properties);

    // track for Braze
    appboy.logCustomEvent(name, properties);

    // track for Google Analytics
    if (properties) {
        ga('send', 'event', {
            eventAction: name,
            eventCategory: (properties as any).category,
            eventLabel: (properties as any).label,
            eventValue: (properties as any).value,
            hitCallback,
        });
    }
}
